import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    Actions,
    Badge,
    Box,
    Button,
    CheckboxStandalone,
    Column,
    Columns,
    IconAdd,
    IconEdit,
    Inline,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    TextField,
    TooltipRenderer,
} from "braid-design-system";
import { ItemLotResponse } from "../../services/itemLotsService";

interface ItemResourceListTableProps {
    itemLotList?: Array<ItemLotResponse> | null;
    itemResourceMap?: Record<string, boolean>;
    approvedItemMap?: Record<string, boolean>;
    searchTerm?: string;
    onSearchChange?: (value: string) => void;
    onCreateItemResource?: () => void;
    onEditItemResource?: (itemLot: ItemLotResponse) => void;
    headerLeftAction?: React.ReactNode;
    selectedItemCodes?: Set<string>;
    onToggleSelectItem?: (itemlotsCode: string) => void;
    onToggleSelectAll?: (itemlotsCodes: string[]) => void;
}

const ItemResourceListTable: React.FC<ItemResourceListTableProps> = ({
    itemLotList,
    itemResourceMap = {},
    approvedItemMap = {},
    searchTerm = "",
    onSearchChange,
    onCreateItemResource,
    onEditItemResource,
    headerLeftAction,
    selectedItemCodes,
    onToggleSelectItem,
    onToggleSelectAll,
}) => {
    const { t } = useTranslation();

    const filteredItemLots = useMemo(() => {
        if (!itemLotList) {
            return [];
        }

        const normalizedSearch = searchTerm.trim().toLowerCase();
        if (!normalizedSearch) return itemLotList;

        return itemLotList.filter((item) => {
            return (
                (item.itemlots_code || "").toLowerCase().includes(normalizedSearch) ||
                (item.itemlots_desc || "").toLowerCase().includes(normalizedSearch) ||
                (item.itemlots_lot || "").toLowerCase().includes(normalizedSearch) ||
                (item.itemlots_barcode || "").toLowerCase().includes(normalizedSearch)
            );
        });
    }, [itemLotList, searchTerm]);

    const showActions = typeof onEditItemResource === "function";
    const showSelection = typeof onToggleSelectItem === "function" && typeof onToggleSelectAll === "function";

    // Only items with at least one resource have anything to send for approval.
    const selectableCodes = useMemo(
        () => filteredItemLots
            .map((item) => item.itemlots_code)
            .filter((code): code is string => !!code && !!itemResourceMap[code]),
        [filteredItemLots, itemResourceMap]
    );
    const selectedCount = selectableCodes.filter((code) => selectedItemCodes?.has(code)).length;
    const allSelected = selectableCodes.length > 0 && selectedCount === selectableCodes.length;
    const headerChecked: boolean | "mixed" = allSelected ? true : selectedCount > 0 ? "mixed" : false;

    return (
        <Stack space="large">
            <Columns space="large" collapseBelow="tablet">
                <Column width="1/3">
                    <Box display="flex" justifyContent="flexStart" alignItems="center">
                        {headerLeftAction ? (
                            headerLeftAction
                        ) : onCreateItemResource ? (
                            <Actions>
                                <Button
                                    variant="ghost"
                                    tone="brandAccent"
                                    icon={<IconAdd />}
                                    onClick={onCreateItemResource}
                                >
                                    {t("page.item_resource_management.list.button.create_new", {
                                        defaultValue: "Create New Resource",
                                    })}
                                </Button>
                            </Actions>
                        ) : null}
                    </Box>
                </Column>
                <Column width="2/3">
                    <Box display="flex" justifyContent="flexEnd" alignItems="center">
                        <TextField
                            placeholder={t("page.item_resource_management.list.search.placeholder", {
                                defaultValue: "Search Items...",
                            })}
                            aria-label={t("page.item_resource_management.list.search.aria", {
                                defaultValue: "Search Items",
                            })}
                            value={searchTerm}
                            onChange={(e) => onSearchChange?.(e.currentTarget.value)}
                        />
                    </Box>
                </Column>
            </Columns>

            <Table
                label={t("page.item_resource_management.list.table.label", {
                    defaultValue: "Item Resource Management List Table",
                })}
            >
                <TableHeader>
                    <TableRow>
                        {showSelection ? (
                            <TableHeaderCell width="content">
                                <CheckboxStandalone
                                    id="item-resource-select-all"
                                    aria-label={t("page.item_resource_management.list.table.headers.select_all", { defaultValue: "Select all" })}
                                    checked={headerChecked}
                                    disabled={selectableCodes.length === 0}
                                    onChange={() => onToggleSelectAll?.(selectableCodes)}
                                />
                            </TableHeaderCell>
                        ) : null}
                        <TableHeaderCell>
                            <Text>{t("page.item_resource_management.list.table.headers.item_code", { defaultValue: "Item Code" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>{t("page.item_resource_management.list.table.headers.description", { defaultValue: "Description" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>{t("page.item_resource_management.list.table.headers.resources", { defaultValue: "Resources" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>{t("page.item_resource_management.list.table.headers.approved", { defaultValue: "Approved" })}</Text>
                        </TableHeaderCell>
                        {showActions ? (
                            <TableHeaderCell align="right">
                                <Text>{t("page.item_resource_management.list.table.headers.actions", { defaultValue: "Actions" })}</Text>
                            </TableHeaderCell>
                        ) : null}
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {filteredItemLots && filteredItemLots.length > 0 ? (
                        filteredItemLots.map((item) => {
                            const code = item.itemlots_code ?? "";
                            const isSelectable = !!code && !!itemResourceMap[code];
                            return (
                            <TableRow key={item.itemlots_code ?? `item-row-${Math.random()}`}>
                                {showSelection ? (
                                    <TableCell width="content">
                                        <CheckboxStandalone
                                            id={`item-resource-select-${code}`}
                                            aria-label={t("page.item_resource_management.list.table.select_item", {
                                                defaultValue: `Select ${item.itemlots_code || "item"}`,
                                            })}
                                            checked={!!selectedItemCodes?.has(code)}
                                            disabled={!isSelectable}
                                            onChange={() => onToggleSelectItem?.(code)}
                                        />
                                    </TableCell>
                                ) : null}
                                <TableCell>
                                    <Text>{item.itemlots_code || t("page.item_resource_management.list.table.na", { defaultValue: "N/A" })}</Text>
                                </TableCell>
                                <TableCell>
                                    <Text>{item.itemlots_desc || item.itemlots_lot || t("page.item_resource_management.list.table.na", { defaultValue: "N/A" })}</Text>
                                </TableCell>
                                <TableCell align="center">
                                    {itemResourceMap[item.itemlots_code ?? ""] ? (
                                        <Badge tone="positive">✓</Badge>
                                    ) : (
                                        <Badge tone="neutral">-</Badge>
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {approvedItemMap[item.itemlots_code ?? ""] ? (
                                        <Badge tone="positive">✓</Badge>
                                    ) : (
                                        <Badge tone="neutral">-</Badge>
                                    )}
                                </TableCell>
                                {showActions ? (
                                    <TableCell align="right">
                                        <Inline space="small">
                                            {onEditItemResource ? (
                                                <TooltipRenderer
                                                    tooltip={<Text>{t("page.item_resource_management.list.tooltip.edit", { defaultValue: "Edit Resources" })}</Text>}
                                                >
                                                    {({ triggerProps }) => (
                                                        <Box
                                                            onClick={() => onEditItemResource(item)}
                                                            cursor="pointer"
                                                            {...triggerProps}
                                                        >
                                                            <IconEdit tone="info" />
                                                        </Box>
                                                    )}
                                                </TooltipRenderer>
                                            ) : null}
                                        </Inline>
                                    </TableCell>
                                ) : null}
                            </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colspan={4 + (showSelection ? 1 : 0) + (showActions ? 1 : 0)} align="center">
                                <Text>{t("page.item_resource_management.list.empty", { defaultValue: "No items found." })}</Text>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Stack>
    );
};

export default ItemResourceListTable;